/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Box,
  Text,
  Flex,
  Link,
  Button,
  ThemeProvider,
  getThemeByName,
  useThemeUI,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import { H } from '@hzdg/sectioning';
import React, { Fragment } from 'react';

import { InterpolatedString } from '../../InterpolatedString';
import { useLocalizedLink } from '../../../utils/use-localized-link';

import { ModalContainer } from './ModalContainer';

import type { FluidObject } from 'gatsby-image';

interface BuyTicketsModal {
  title?: string;
  subTitle?: string | null;
  description?: string | null;
  cta?: {
    label?: string | null;
    url?: string | null;
  } | null;
  link?: {
    label?: string;
    url?: string;
  };
  fluid?: FluidObject;
  alt?: string;
  closeModal: () => void;
  showCloseLink?: boolean;
  icon?: boolean;
  ticketCount: number;
}

export const ModalCard = ({
  closeModal,
  title,
  subTitle,
  description,
  cta,
  link,
  showCloseLink = true,
  icon,
  ticketCount,
}: BuyTicketsModal): JSX.Element => {
  const getLocalizedLink = useLocalizedLink();
  const themeUI = useThemeUI();

  const handleCloseModel = () => {
    closeModal();
  };

  const handleCta = () => {
    closeModal();
  };

  return (
    <ModalContainer closeModal={handleCloseModel} data-testid="close-modal">
      <Box
        sx={{
          py: [4, 5, 7],
          pb: [4, 5, 8],
          px: [4, 6],
        }}
      >
        <Flex sx={{ alignItems: 'center' }}>
          {icon && (
            <ThemeProvider theme={getThemeByName('Top of the Rock Blue')}>
              <Icons.Clock style={{ color: themeUI.theme.colors.background }} />
            </ThemeProvider>
          )}
          <H
            sx={{
              ml: icon ? 2 : 0,
              variant: 'text.mediumTitle',
            }}
          >
            {title}
          </H>
        </Flex>
        <Text sx={{ variant: 'styles.h5' }}>{subTitle}</Text>
        {!!description && (
          <InterpolatedString
            renderChunk={(chunk) => {
              if (typeof chunk !== 'string') {
                return chunk;
              }

              return (
                <Fragment>
                  {chunk.split('\n\n').map((subchunk, i) => (
                    <Text
                      as="p"
                      key={i}
                      sx={{
                        marginY: 3,
                        paddingTop: i === 0 ? 1 : 0,
                      }}
                      variant="mediumP"
                    >
                      {subchunk}
                    </Text>
                  ))}
                </Fragment>
              );
            }}
          >
            {description}
          </InterpolatedString>
        )}
        <Flex
          sx={{
            mt: 5,
            flexDirection: 'column',
            width: 'fit-content',
            alignItems: 'center',
          }}
        >
          {cta?.url ? (
            <Link
              href={getLocalizedLink(cta.url)}
              onClick={handleCta}
              sx={{ textAlign: 'center' }}
              variant="buttonInverted"
            >
              {cta.label}
            </Link>
          ) : null}
        </Flex>
      </Box>
    </ModalContainer>
  );
};
